/**
 * Der Admin-Schlüssel im Browser.
 *
 * **`sessionStorage` und nicht `localStorage` — bewusst anders als beim Sitzungs-Token.** Der
 * Admin-Schlüssel ist der mächtigste Wert dieser Anwendung: er ändert die globalen Balancewerte,
 * erzeugt und widerruft Host-Links und öffnet ab Phase 11 den Debug-Modus, also eine Cheat-API
 * (`CLAUDE.md`, Berechtigungsmodell). Ein geschlossener Tab soll ihn deshalb vergessen. Der
 * Sitzungs-Token eines Spielers liegt im `localStorage`, weil §46 verlangt, dass ein Spieler nach
 * einem Abbruch **als derselbe Spieler** zurückkommt — zwei verschiedene Anforderungen, also
 * zwei verschiedene Speicher.
 *
 * **Er steht in keiner Adresse** (`routing/fragment.ts`, `ARCHITECTURE.md` **E26**). Ein Link mit
 * dem Admin-Schlüssel stünde im Verlauf jedes Browsers, über den er je geöffnet wurde, und wäre
 * dauerhaft gültig — er kommt aus einem Formular.
 *
 * **Jeder Zugriff ist umschlossen.** In einem privaten Fenster oder bei blockierten
 * Website-Daten wirft schon das Lesen; dann arbeitet die Oberfläche ohne Wiedererkennung
 * weiter — derselbe Umgang wie in `session.ts`.
 */

/** Der Schlüssel im `sessionStorage`. Mit Namensraum, und ein anderer als der der Spielsitzung. */
export const ADMIN_KEY_STORAGE = 'flaghex.adminKey';

/**
 * Der gespeicherte Admin-Schlüssel, oder `undefined`.
 *
 * Leerraum wird abgeschnitten: ein von Hand eingefügter Schlüssel bringt leicht ein Leerzeichen
 * mit, und als Header wäre er damit ein anderer Wert — die Antwort hieße „nicht berechtigt", und
 * niemand sucht ein Leerzeichen.
 */
export function readAdminKey(): string | undefined {
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(ADMIN_KEY_STORAGE);
  } catch {
    return undefined;
  }
  const key = stored?.trim() ?? '';
  return key === '' ? undefined : key;
}

/**
 * Speichert den Schlüssel für diesen Tab.
 *
 * Ein leerer Schlüssel ist ein **Fehler im Programm** und wird laut abgewiesen: still gespeichert
 * ergäbe er einen geöffneten Zustand ohne Berechtigung, und der Fehler fiele erst bei der
 * nächsten Anfrage auf. Ein blockierter Speicher ist dagegen kein Fehler des Programms und wird
 * geschluckt.
 */
export function writeAdminKey(key: string): void {
  const trimmed = key.trim();
  if (trimmed === '') {
    throw new Error('Ein leerer Admin-Schlüssel ist kein Schlüssel');
  }
  try {
    window.sessionStorage.setItem(ADMIN_KEY_STORAGE, trimmed);
  } catch {
    // Absichtlich still: siehe Modulkopf.
  }
}

/** Vergisst den Schlüssel — nach einer abgewiesenen Anfrage oder auf Wunsch. */
export function clearAdminKey(): void {
  try {
    window.sessionStorage.removeItem(ADMIN_KEY_STORAGE);
  } catch {
    // Absichtlich still: siehe Modulkopf.
  }
}
